import { _ } from "../../../core/polyglot/polyglot.js";
import { formatFullWithTwoDecimalsNoCurrency } from "../../../core/helpers/currency-formatting-util.js";
import {
	formatNoDecimal,
	formatThousandWithoutAbbreviation,
} from "../../../core/helpers/count-formatting-util.js";
import accountRowVisibilityFactory from "../accountRowVisibilities/accountRowVisibilityFactory.js";
import Model from "../../Model.js";

/**
 * Store various display settings of a report.
 *
 * @param {abstractAccountVisibility} [spec.accountVisibility] - Show accounts in report. The
 * default is to hide account rows with no data.
 * @param {boolean} [spec.showSummaryRows] - Show summary rows for group rows
 * @param {function} [spec.formatter] - Formatter for report values
 * @param {function} [spec.debitPositive] - Whether the formatted value signs should be inversed
 * @param {function} [spec.landscape] - Whether the export media will be in landscape mode
 * @param {integer} [spec.defaultExpansionLevel] - Indicates at which
 * level a report should get expanded by default: i.e., the maximum
 * depth at which a report's row should be visible.
 */
export default class ReportDisplaySettings extends Model {
	constructor({
		accountVisibility,
		showSummaryRows = false,
		debitPositive = false,
		formatter = formatNoDecimal,
		landscape = false,
		defaultExpansionLevel = 2,
		showLogo = true,
	} = {}) {
		super(...arguments);

		this._accountVisibility =
			accountVisibility ||
			accountRowVisibilityFactory.defaultVisibility();

		this._showSummaryRows = showSummaryRows;
		this._formatter = formatter;
		this._debitPositive = debitPositive;
		this._landscape = landscape;
		this._defaultExpansionLevel = defaultExpansionLevel;
		this._showLogo = showLogo;
	}

	getAccountVisibility() {
		return this._accountVisibility;
	}

	setAccountVisibility(accountVisibility) {
		this._accountVisibility = accountVisibility;
	}

	getShowSummaryRows() {
		return this._showSummaryRows;
	}

	setShowSummaryRows(showSummaryRows) {
		this._showSummaryRows = showSummaryRows;
	}

	getFormatter() {
		return this._formatter;
	}

	setFormatter(formatter) {
		this._formatter = formatter;
	}

	getDebitPositive() {
		return this._debitPositive;
	}

	setDebitPositive(debitPositive) {
		this._debitPositive = debitPositive;
	}

	getLandscape() {
		return this._landscape;
	}

	setLandscape(landscape) {
		this._landscape = landscape;
	}

	getDefaultExpansionLevel() {
		return this._defaultExpansionLevel;
	}

	setDefaultExpansionLevel(newLevel) {
		this._defaultExpansionLevel = newLevel;
	}

	getShowLogo() {
		return this._showLogo;
	}

	setShowLogo(showLogo) {
		this._showLogo = showLogo;
	}

	/**
	 * Return a clone of the receiver.
	 *
	 * This is used when instantiating a report, to make sure that reports
	 * have a copy of their template's display settings, so that display
	 * modifications do not impact the template.
	 *
	 * @return {reportDisplaySettings} - A shallow copy of the display settings.
	 */
	clone() {
		return new this.constructor({
			accountVisibility: this._accountVisibility,
			showSummaryRows: this._showSummaryRows,
			formatter: this._formatter,
			debitPositive: this._debitPositive,
			landscape: this._landscape,
			showLogo: this._showLogo,
		});
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.formatter = this.constructor
			.allFormatters()
			.indexOf(this._formatter);

		properties.accountVisibility = accountRowVisibilityFactory.serialize(
			this._accountVisibility,
		);

		if (this._defaultExpansionLevel === Infinity) {
			properties.defaultExpansionLevel = -1;
		} else {
			properties.defaultExpansionLevel = this._defaultExpansionLevel;
		}

		properties.showSummaryRows = this._showSummaryRows;
		properties.debitPositive = this._debitPositive;
		properties.landscape = this._landscape;
		properties.showLogo = this._showLogo;

		return properties;
	}

	static transformMaterializationData(data) {
		data.formatter = this.allFormatters()[data.formatter || 0];

		data.accountVisibility = accountRowVisibilityFactory.materialize(
			data.accountVisibility,
		);

		if (data.defaultExpansionLevel === -1) {
			data.defaultExpansionLevel = Infinity;
		}
	}

	static allFormatterSpecs() {
		return [
			{
				id: "show-decimals",
				label: _("Show decimals"),
				formatter: formatFullWithTwoDecimalsNoCurrency,
			},
			{
				id: "no-decimals",
				label: _("No decimals"),
				formatter: formatNoDecimal,
			},
			{
				id: "shorten-thousands",
				label: _("Shorten thousands"),
				formatter: formatThousandWithoutAbbreviation,
			},
		];
	}

	static allFormatters() {
		return this.allFormatterSpecs().map(({ formatter }) => formatter);
	}
}
